import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import usePageSeo from "@/hooks/usePageSeo";

// Kick off a password reset. Backend always returns 200 so we never leak
// whether the email exists — this page shows the same success card either way.
export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  usePageSeo({
    title: "Reset your password — Dividend Portfolio Planner",
    description: "Forgot your DPP password? Enter your email and we'll send you a secure one-time reset link. Links expire in 60 minutes.",
    path: "/forgot-password",
    noindex: true,
  });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/password/reset-request", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch {
      // Even a transient error, we don't want to leak details — show generic
      // success. Real network errors will surface via the toast below.
      toast.error("Something went wrong. Please try again in a moment.");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-16">
      <button
        onClick={() => nav("/auth")}
        className="inline-flex items-center gap-1.5 mono text-[11px] uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors mb-6"
        data-testid="forgot-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
      </button>

      <div className="text-center mb-8">
        <div className="mono text-[11px] uppercase tracking-[0.25em] text-secondary">Password reset</div>
        <h1 className="serif text-4xl sm:text-5xl mt-3">Forgot your password<span className="text-secondary">?</span></h1>
      </div>

      <Card data-testid="forgot-card">
        <CardContent className="p-8">
          {sent ? (
            <div className="text-center space-y-4" data-testid="forgot-sent">
              <div className="w-14 h-14 rounded-full bg-primary/10 text-primary grid place-items-center mx-auto">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <div>
                <div className="serif text-2xl">Check your inbox</div>
                <p className="text-sm text-muted-foreground mt-2">
                  If an account exists for <strong className="text-foreground">{email}</strong>, a reset link is on its way. It expires in <strong>60 minutes</strong>.
                </p>
                <p className="text-xs text-muted-foreground mt-3">
                  Not seeing it? Check spam, or wait a minute and try again — we cap resets at 3 per hour so nobody can spam your inbox.
                </p>
              </div>
              <Button variant="outline" className="rounded-full" onClick={() => nav("/auth")} data-testid="forgot-back-to-signin">
                Back to sign in
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Enter the email you used to sign up. We&apos;ll email you a link to pick a new password.
              </p>
              <div>
                <Label htmlFor="fp-email">Email</Label>
                <Input
                  id="fp-email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="forgot-email"
                />
              </div>
              <Button
                type="submit"
                disabled={busy || !email.trim()}
                className="w-full rounded-full"
                data-testid="forgot-submit"
              >
                {busy
                  ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending link…</>
                  : <><Mail className="w-4 h-4 mr-2" /> Send reset link</>}
              </Button>
              <div className="text-center text-xs text-muted-foreground pt-1">
                Remembered it?{" "}
                <Link to="/auth" className="text-primary hover:underline underline-offset-2">Sign in</Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
